import { isEmpty } from '@/helpers/is';

const noop = () => null;

/**
 * validate is value not empty
 * @param {string} val
 * @returns {boolean}
 */
const required = (val) => !isEmpty(val);

/**
 * validate only numbers
 * @param {string} val
 * @returns {boolean}
 */
const numbers = (val) => /^[0-9]+$/.test(val);

/**
 * validate only numbers
 * @param {string} val
 * @returns {boolean}
 */
const decimalNumber = (val) => /^\d+(\.\d+)?$/.test(String(val));

/**
 * Validate percentage value (0 to 100, including decimals)
 * @param {string|number} val
 * @returns {boolean}
 */
const percentage = (val) => {
  const num = parseFloat(val);
  return /^\d+(\.\d+)?$/.test(String(val)) && num >= 0 && num <= 100;
};

/**
 * validate only english characters
 * @param {string} val
 * @returns {boolean}
 */
const english = (val) => /[a-zA-Z]/.test(val);

/**
 * Validate only Hebrew characters and spaces
 * @param {string} val
 * @returns {boolean}
 */
const hebrew = (val) => /^[\u0590-\u05FF\s]+$/.test(val);

/**
 * validate only characters
 * @param {string} val
 * @returns {boolean}
 */
const characters = (val) => english(val) || hebrew(val);

/**
 * validate israel mobile phone number
 * @param {string} val
 * @returns {boolean}
 */
const mobile = (val) => /^0(5[^7]|[2-4]|[8-9]|7[0-9])[0-9]{7}$/.test(val);

/**
 * check if variable is phone number
 * @param {string} val
 * @returns {boolean}
 */
const phone = (val) => /^(?:0(([23489]\d{7})|(5[0-9]\d{7})|(77\d{7})))$/.test(val);

/**
 * validate email address
 * @param {string} val
 * @returns {boolean}
 */
const email = (val) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val);

/**
 * Validate if the input is a valid zip code.
 * @param {string} val - The string to validate.
 * @returns {boolean} True if the input is a numeric string with 5 to 7 digits.
 */
const zipCode = (val) => /^\d{5,7}$/.test(val);

/**
 * validate year
 * @param {string} val
 * @returns {boolean}
 */
const year = (val) => /^[12]\d{3}$/.test(val);

/**
 * Validate time
 * @param {string} val
 * @returns {boolean}
 */
const time = (val) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(val);

/**
 * Validate date in DD/MM/YYYY format and ensure it's not in the future
 * @param {string} val
 * @returns {boolean}
 */
const date = (val) => {
  const regex = /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
  if (!regex.test(val)) {
    return false;
  }
  return true;
};

/**
 * validate israel id number
 * @param {string | number} val
 * @returns {boolean}
 */
const uid = (val) => {
  let id = String(val).trim();
  id = id.length < 9 ? (`00000000${id}`).slice(-9) : id;
  if (id.length > 9 || Number.isNaN(id)) return false;
  return Array.from(id, Number).reduce((counter, digit, i) => {
    const step = digit * ((i % 2) + 1);
    return counter + (step > 9 ? step - 9 : step);
  }) % 10 === 0;
};
const hebrewWithPunctuation = (val) => /^[\u05D0-\u05EA׳\s\d.,;?!:()"'-]+$/.test(val);

const validations = {
  required,
  mobile,
  email,
  uid,
  numbers,
  decimalNumber,
  percentage,
  english,
  hebrew,
  characters,
  year,
  phone,
  time,
  date,
  zipCode,
  hebrewWithPunctuation,
};

/**
 * wrapper function for mantine form validation
 * @param {mixed} value
 * @param {object} data
 * @returns {string | null}
 */
const validate = (value, data = {}) => {
  let output = null;
  Object.keys(data).forEach((key) => {
    const error = data[key];
    const func = validations[key] || noop;

    // continue in case we already have output
    if (output) {
      return;
    }
    output = func(value) ? null : error;
  });
  return output;
};

export default validate;
export {
  numbers,
  english,
  hebrew,
  characters,
};
